import { useState, useEffect } from 'react';
import { openedLeadCard, checkHasPaymentMethod, createDemoReminder, getUserCompany, getPackageItemsRemainingCount, createBuyerDemo, getSellerInvitesSent, declineDemoSeller, cancelSellerInvite} from '../../utils/api';
import { refresh, getCurrentUser } from '../../services/auth.service';
import { FaRegEnvelope, FaTimes, FaPaperPlane, FaRegCheckCircle } from "react-icons/fa";
import { Send, Calendar, Globe, Building2, UserCircle2, XCircle } from 'lucide-react';
import Spinner from '../../components/layout/Spinner';
import { Link, useNavigate } from 'react-router-dom';
import FadeIn from '../../utils/fadein';
import { getBaseImageUrl, getTrimmedString } from '../../utils/utils';

const SellerInvites = () => {
  const imagePath = process.env.REACT_APP_IMAGE_PATH;
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [userCompany, setUserCompany] = useState(null);
  const [data, setData] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [recommendedLoaded, setRecommendedLoaded] = useState(false);
  const [showBuyerInvite, setShowBuyerInvite] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [showInviteConfirm, setShowInviteConfirm] = useState(false);
  const [numberShowing, setNumberShowing] = useState(7);
  const [showLeavingWebsite, setShowLeavingWebsite] = useState(false);
  const [externalWebsite, setExternalWebsite] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState(null);
  const imageUrl = process.env.REACT_APP_IMAGE_URL;
  const baseImageUrl = getBaseImageUrl();
  const navigate = useNavigate();

  useEffect(async () => {
    async function loadData(){
      let res = await getSellerInvitesSent();

      if (res.success){
        setData(res.data);
        setLoaded(true);
      }
    }

    async function loadUserCompany(){
      let res = await getUserCompany();

      if (res.success){
        setUserCompany(res.data);
      }

      else if (res.status === 403){
        await refresh();

        let res = await getUserCompany();

        if (res.success){
          setUserCompany(res.data);
        }
      }
    }

    await loadData();
    await loadUserCompany();
  }, []);

  const handleCancelRequest = (invite) => {
    setSelectedInvite(invite);
    setShowCancelDialog(true);
  };

  const confirmCancelRequest = async () => {
    // TODO: Add API call to cancel request
    let res = await cancelSellerInvite(selectedInvite.id);

    if (res.success) {
      setShowCancelDialog(false);
      setSelectedInvite(null);

      // remove demo from data
      setData(data.filter(item => item.demo_id !== selectedInvite.demo_id));
    }
  };

  return (
    <>
      {loaded === false && (
        <Spinner />
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loaded && (
          <div className="space-y-8">
            <div className="border-b border-gray-200 pb-6">
              <div className="flex items-center space-x-3">
                <Send className="h-8 w-8 text-indigo-600" />
                <h1 className="text-2xl font-bold tracking-tight text-gray-900">Sent Demo Requests</h1>
              </div>
              <p className="mt-2 text-sm text-gray-500">Track and manage all the demo requests you've sent to potential buyers.</p>
            </div>

            {data.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <p className="text-gray-500">
                  You haven't sent any demo requests yet. Browse through our <Link className="text-indigo-600 hover:text-indigo-500" to="/buyer-directory">recommended buyers</Link> to find potential matches for your product.
                </p>
              </div>
            )}

            {data.length > 0 && (
              <FadeIn delay={200} duration={200}>
                <div className="overflow-hidden bg-white shadow ring-1 ring-gray-200 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date Sent
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Buyer Information
                        </th>

                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Company Info
                          </th>

                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200">
                      {data.slice(0, numberShowing).map(item => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-500 sm:pl-6">
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4" />
                              <span>
                                {new Date(item.date_sent).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-4">
                              <div className="h-12 w-12 flex-shrink-0">
                                <img
                                  className="h-12 w-12 rounded-full object-cover"
                                  src={imageUrl + 'tr:di-@@companylogos@@default-buyer.png/' + item.buyer_logo}
                                  alt=""
                                />
                              </div>
                              <div>
                                <div className="font-medium text-gray-900 flex items-center space-x-2">
                                  <UserCircle2 className="h-4 w-4" />
                                  <span>{item.first_name} {item.last_name}</span>
                                </div>
                                <div className="text-gray-500 flex items-center space-x-2 mt-1">
                                  <Building2 className="h-4 w-4" />
                                  <span>{item.buyer_job_title} at {item.buyer_company_name}</span>
                                </div>
                                {item.description && (
                                  <div className="mt-1 text-sm text-gray-500">
                                    {getTrimmedString(item.description)}
                                  </div>
                                )}
                                {item.website && (
                                  <div className="mt-2">
                                    <button
                                      onClick={() => {
                                        setExternalWebsite(item.website.startsWith('http') ? item.website : 'https://' + item.website);
                                        setShowLeavingWebsite(true);
                                      }}
                                      className="inline-flex items-center space-x-1 text-sm text-indigo-600 hover:text-indigo-500"
                                    >
                                      <Globe className="h-4 w-4" />
                                      <span>Company website</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4 text-sm text-gray-500">
                            <div>
                              {item.industry_name}
                            </div>

                            <div>
                              {item.company_size}
                            </div>
                          </td>

                          <td className="whitespace-nowrap py-4 pr-6 text-right text-sm font-medium">
                            <button
                              className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              onClick={() => handleCancelRequest(item)}
                            >
                              <FaTimes className="h-4 w-4 mr-2" />
                              Cancel Request
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </FadeIn>
            )}
          </div>
        )}
      </div>

      {showCancelDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="flex items-center justify-center text-red-600 mb-4">
              <XCircle className="h-12 w-12" />
            </div>
            <h3 className="text-lg font-medium text-center mb-4">Cancel Demo Request</h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              Are you sure you want to cancel your demo request to {selectedInvite?.first_name} {selectedInvite?.last_name}? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                onClick={() => setShowCancelDialog(false)}
              >
                No, keep it
              </button>
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                onClick={confirmCancelRequest}
              >
                Yes, cancel it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

  export default  SellerInvites;